/**
 * `OutbreakMap` — interactive geospatial map of an outbreak.
 *
 * Renders a MapLibre GL canvas with three concurrent layers:
 *   - **Country / region markers** — circles whose radius and colour scale
 *     with case count per region (from `regions.list`).
 *   - **Ship track polyline** — GeoJSON LineString from `ship.getTrack`,
 *     drawn in the emergency-red token.
 *   - **Live ship marker** — DOM-based `maplibregl.Marker` with a pulsing
 *     dot. The pulse is allowed under uncodixify-ui because it is a
 *     status indicator, not a decorative animation on the surrounding
 *     container.
 *
 * Live updates: a `/sse/ships/:mmsi` subscription patches the GeoJSON
 * source and DOM marker in place — React never re-renders the canvas.
 * That is the entire reason the MapLibre instance lives in a ref rather
 * than React state.
 *
 * SSR safety: MapLibre touches `window` and `HTMLCanvasElement` at
 * construction time, so the instance is created inside `useEffect`.
 * The component renders a skeleton placeholder during the brief window
 * before the style finishes loading (200-500ms typical).
 *
 * Fallback: if MapLibre fails to load (CDN outage, blocked tiles, WebGL
 * disabled), we surface a static text fallback with a link to the
 * regional table further down the page — never a broken canvas.
 */

import type { Feature, FeatureCollection, LineString, Point } from "geojson";
import maplibregl, {
  type GeoJSONSource,
  type LngLatLike,
  type Map as MapLibreMap,
  type Marker as MapLibreMarker,
  type Popup as MapLibrePopup,
} from "maplibre-gl";
import { useEffect, useRef, useState } from "react";

import { useSSE } from "#/hooks/useSSE.js";
import type { TranslateFn } from "#/lib/i18n.js";

/** A region marker rendered on the country circle layer. */
export type OutbreakMapRegion = {
  id: number | string;
  name: string;
  /** Optional descriptive text shown in the popup. */
  dataText?: string | null;
  /** Used to scale circle radius. Treated as 0 when null/undefined. */
  caseCount?: number | null;
  /** [lng, lat] — GeoJSON convention. */
  coordinates: [number, number];
  /** Optional href the popup link should target. */
  detailHref?: string;
  /** Optional risk classification — drives circle colour. */
  risk?: "very-high" | "high" | "moderate" | "low" | null;
};

/** Initial track + current position for one tracked vessel. */
export type OutbreakMapShip = {
  mmsi: string;
  name: string;
  /** Initial track coordinates `[lng, lat]`, ordered oldest -> newest. */
  trackCoordinates: Array<[number, number]>;
  /** Optional current position; falls back to last point of `trackCoordinates`. */
  currentPosition?: { lat: number; lng: number };
};

export type OutbreakMapProps = {
  /** All region markers for this outbreak. Empty array is fine. */
  regions: OutbreakMapRegion[];
  /** Optional vessel to track live. Omit for outbreaks with no ship. */
  ship?: OutbreakMapShip | null;
  /** Locale-resolved labels and aria text. Provided by parent page. */
  t: TranslateFn;
  /** UI locale, propagated as `?locale=` to the SSE endpoint. */
  locale: string;
  /** Optional override for the API base URL — used by tests. */
  apiBaseUrl?: string;
};

/** Wire shape from `/sse/ships/:mmsi`. */
type ShipPositionEvent = {
  mmsi: string;
  name?: string;
  lat: number;
  lng: number;
  speedKnots?: number | null;
  headingDegrees?: number | null;
  recordedAt?: string;
};

/** OpenFreeMap dark style — free, no API key, dark base palette. */
const MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/dark";

/** Default centre + zoom — global view; `flyTo` re-centres after data loads. */
const DEFAULT_CENTER: LngLatLike = [0, 20];
const DEFAULT_ZOOM = 2;

/** Cap on the ship-track polyline length to bound payload + render cost. */
const MAX_TRACK_POINTS = 500;

/** Risk -> CSS-token colour map for the country circle layer. */
const RISK_COLOURS: Record<NonNullable<OutbreakMapRegion["risk"]>, string> = {
  "very-high": "hsl(354 78% 52%)",
  high: "hsl(20 90% 56%)",
  moderate: "hsl(36 92% 54%)",
  low: "hsl(152 56% 42%)",
};
const DEFAULT_REGION_COLOUR = "hsl(354 78% 52%)";

function regionsToFeatureCollection(regions: OutbreakMapRegion[]): FeatureCollection<Point> {
  return {
    type: "FeatureCollection",
    features: regions.map<Feature<Point>>((r) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: r.coordinates },
      properties: {
        id: r.id,
        name: r.name,
        dataText: r.dataText ?? "",
        caseCount: typeof r.caseCount === "number" ? r.caseCount : 0,
        risk: r.risk ?? null,
        detailHref: r.detailHref ?? "",
      },
    })),
  };
}

function trackToFeature(coords: Array<[number, number]>): Feature<LineString> {
  return {
    type: "Feature",
    geometry: { type: "LineString", coordinates: coords.slice() },
    properties: {},
  };
}

function buildShipMarkerElement(): HTMLDivElement {
  const el = document.createElement("div");
  el.className = "ship-marker";
  el.setAttribute("role", "img");
  // The pulse + dot inner elements are styled in `index.css`. We split
  // them so the pulse animation lives on its own div and the static dot
  // stays crisp on top.
  const pulse = document.createElement("span");
  pulse.className = "ship-marker-pulse";
  pulse.setAttribute("aria-hidden", "true");
  const dot = document.createElement("span");
  dot.className = "ship-marker-dot";
  dot.setAttribute("aria-hidden", "true");
  el.appendChild(pulse);
  el.appendChild(dot);
  return el;
}

/**
 * Custom MapLibre control: a small "Reset view" button that calls
 * `flyTo(initialCenter)`. Uses the same icon-button styling as the
 * built-in zoom controls so it looks native.
 */
class ResetViewControl implements maplibregl.IControl {
  private readonly center: LngLatLike;
  private readonly zoom: number;
  private readonly label: string;
  private container: HTMLDivElement | null = null;
  private map: MapLibreMap | null = null;

  constructor(center: LngLatLike, zoom: number, label: string) {
    this.center = center;
    this.zoom = zoom;
    this.label = label;
  }

  onAdd(map: MapLibreMap): HTMLElement {
    this.map = map;
    const container = document.createElement("div");
    container.className = "maplibregl-ctrl maplibregl-ctrl-group";
    const button = document.createElement("button");
    button.type = "button";
    button.className = "outbreak-map-reset-btn";
    button.setAttribute("aria-label", this.label);
    button.title = this.label;
    button.textContent = "⌂";
    button.addEventListener("click", () => {
      this.map?.flyTo({
        center: this.center,
        zoom: this.zoom,
        essential: true,
      });
    });
    container.appendChild(button);
    this.container = container;
    return container;
  }

  onRemove(): void {
    this.container?.parentNode?.removeChild(this.container);
    this.container = null;
    this.map = null;
  }
}

export function OutbreakMap({ regions, ship, t, locale, apiBaseUrl }: OutbreakMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const shipMarkerRef = useRef<MapLibreMarker | null>(null);
  /** Live mutation target — track coords stored in a plain array so we
   * can mutate without React rerenders. */
  const trackCoordsRef = useRef<Array<[number, number]>>([]);
  const popupRef = useRef<MapLibrePopup | null>(null);

  // Mirror the latest props into refs so the mount-only useEffect can
  // seed the map with current values without re-running on every prop
  // change. Subsequent updates flow through the data-sync effects below.
  const initialRegionsRef = useRef(regions);
  initialRegionsRef.current = regions;
  const initialShipRef = useRef(ship);
  initialShipRef.current = ship;
  const tRef = useRef(t);
  tRef.current = t;

  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  // ------------------------------------------------------------------
  // 1. Map construction (mount only).
  // ------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    let map: MapLibreMap;
    try {
      map = new maplibregl.Map({
        container,
        style: MAP_STYLE_URL,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        attributionControl: { compact: true },
        // 3D pitch / bearing is unused; disabling saves render cost on
        // low-end mobile GPUs.
        pitch: 0,
        bearing: 0,
        pitchWithRotate: false,
        dragRotate: false,
        // The Hantavirus map covers the world once — repeating world
        // copies waste memory and confuse the click → popup mapping.
        renderWorldCopies: false,
      });
    } catch {
      setHasFailed(true);
      return undefined;
    }

    mapRef.current = map;

    // Built-in controls. We add navigation in the top-right (default);
    // RTL flip is handled via CSS overrides (see `index.css`).
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-right");
    map.addControl(new maplibregl.FullscreenControl(), "top-right");
    map.addControl(
      new ResetViewControl(DEFAULT_CENTER, DEFAULT_ZOOM, tRef.current("map.resetView")),
      "top-right",
    );

    map.on("error", (e) => {
      console.warn("[OutbreakMap]", e?.error?.message ?? e);
    });

    map.on("load", () => {
      // -- Country / region marker source + circle layer ---------------
      map.addSource("regions", {
        type: "geojson",
        data: regionsToFeatureCollection(initialRegionsRef.current),
        cluster: true,
        clusterMaxZoom: 6,
        clusterRadius: 40,
      });

      // Cluster bubbles — fall back to red token; size ramps with point count.
      map.addLayer({
        id: "regions-clusters",
        type: "circle",
        source: "regions",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": DEFAULT_REGION_COLOUR,
          "circle-stroke-color": "hsl(220 14% 14%)",
          "circle-stroke-width": 2,
          "circle-radius": ["step", ["get", "point_count"], 14, 10, 18, 50, 24],
          "circle-opacity": 0.75,
        },
      });

      map.addLayer({
        id: "regions-cluster-count",
        type: "symbol",
        source: "regions",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-size": 12,
          "text-font": ["Noto Sans Regular"],
        },
        paint: {
          "text-color": "hsl(0 0% 100%)",
        },
      });

      // Individual region circles — colour by risk, size by caseCount.
      map.addLayer({
        id: "regions-points",
        type: "circle",
        source: "regions",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": [
            "match",
            ["coalesce", ["get", "risk"], "high"],
            "very-high",
            RISK_COLOURS["very-high"],
            "high",
            RISK_COLOURS.high,
            "moderate",
            RISK_COLOURS.moderate,
            "low",
            RISK_COLOURS.low,
            DEFAULT_REGION_COLOUR,
          ],
          "circle-stroke-color": "hsl(220 14% 14%)",
          "circle-stroke-width": 2,
          // Radius scales with sqrt(caseCount) so very large outbreaks
          // don't dominate the canvas, but tiny ones stay visible.
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["sqrt", ["coalesce", ["get", "caseCount"], 1]],
            0,
            6,
            10,
            12,
            50,
            22,
          ],
          "circle-opacity": 0.85,
        },
      });

      // Click handler — open popup on the region marker.
      map.on("click", "regions-points", (e) => {
        const feature = e.features?.[0];
        if (!feature || feature.geometry.type !== "Point") return;
        const props = feature.properties ?? {};
        const coords = (feature.geometry as Point).coordinates;
        const lng = coords[0] ?? 0;
        const lat = coords[1] ?? 0;
        const html = renderRegionPopup(
          {
            name: typeof props.name === "string" ? props.name : "",
            dataText: typeof props.dataText === "string" && props.dataText ? props.dataText : null,
            detailHref:
              typeof props.detailHref === "string" && props.detailHref ? props.detailHref : null,
          },
          tRef.current,
        );
        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({
          closeButton: true,
          closeOnClick: true,
          maxWidth: "320px",
        })
          .setLngLat([lng, lat])
          .setHTML(html)
          .addTo(map);
      });

      // Cluster click — zoom in.
      map.on("click", "regions-clusters", (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const clusterId = (feature.properties as { cluster_id?: number })?.cluster_id;
        const source = map.getSource("regions") as GeoJSONSource;
        if (typeof clusterId !== "number") return;
        source.getClusterExpansionZoom(clusterId).then((zoom) => {
          map.easeTo({
            center: (feature.geometry as Point).coordinates as LngLatLike,
            zoom,
          });
        });
      });

      // Cursor affordance.
      for (const layer of ["regions-points", "regions-clusters"]) {
        map.on("mouseenter", layer, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", layer, () => {
          map.getCanvas().style.cursor = "";
        });
      }

      // -- Ship track + marker ----------------------------------------
      const initialShip = initialShipRef.current;
      if (initialShip) {
        const initial = initialShip.trackCoordinates.slice(-MAX_TRACK_POINTS);
        trackCoordsRef.current = initial;

        map.addSource("ship-track", {
          type: "geojson",
          data: trackToFeature(initial),
        });
        map.addLayer({
          id: "ship-track-line",
          type: "line",
          source: "ship-track",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "hsl(354 78% 52%)",
            "line-width": 3,
            "line-opacity": 0.8,
          },
        });

        const last = initial[initial.length - 1];
        const startLngLat: LngLatLike = initialShip.currentPosition
          ? [initialShip.currentPosition.lng, initialShip.currentPosition.lat]
          : (last ?? DEFAULT_CENTER);

        const markerEl = buildShipMarkerElement();
        markerEl.setAttribute(
          "aria-label",
          tRef.current("map.shipMarkerLabel", { name: initialShip.name }),
        );
        const marker = new maplibregl.Marker({ element: markerEl })
          .setLngLat(startLngLat)
          .addTo(map);
        shipMarkerRef.current = marker;

        // Centre on the most recent fix so users see the vessel
        // immediately rather than the default global view.
        map.flyTo({
          center: startLngLat,
          zoom: Math.max(map.getZoom(), 4),
          essential: true,
        });
      }

      setIsStyleLoaded(true);
    });

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      shipMarkerRef.current?.remove();
      shipMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // Mount-only — `initialRegionsRef`, `initialShipRef`, and `tRef`
    // capture the latest props without retriggering the effect. Prop
    // changes propagate through the data-sync effects below.
  }, []);

  // ------------------------------------------------------------------
  // 2. Sync `regions` prop into the map source on change.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded) return;
    const source = map.getSource("regions") as GeoJSONSource | undefined;
    if (!source) return;
    source.setData(regionsToFeatureCollection(regions));
  }, [regions, isStyleLoaded]);

  // ------------------------------------------------------------------
  // 3. Sync initial `ship` prop changes (e.g., new MMSI) — rebuild track.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded || !ship) return;
    const source = map.getSource("ship-track") as GeoJSONSource | undefined;
    if (!source) return;
    const next = ship.trackCoordinates.slice(-MAX_TRACK_POINTS);
    trackCoordsRef.current = next;
    source.setData(trackToFeature(next));
  }, [ship, isStyleLoaded]);

  // ------------------------------------------------------------------
  // 4. Live position stream.
  // ------------------------------------------------------------------
  const sseUrl = ship ? buildSSEUrl(ship.mmsi, locale, apiBaseUrl) : "";
  useSSE<"ship.position", ShipPositionEvent>(
    sseUrl,
    {
      "ship.position": (data) => {
        if (!data || typeof data.lat !== "number" || typeof data.lng !== "number") {
          return;
        }
        const map = mapRef.current;
        if (!map) return;
        // 1. Append to track ring buffer.
        trackCoordsRef.current.push([data.lng, data.lat]);
        if (trackCoordsRef.current.length > MAX_TRACK_POINTS) {
          trackCoordsRef.current.shift();
        }
        const source = map.getSource("ship-track") as GeoJSONSource | undefined;
        source?.setData(trackToFeature(trackCoordsRef.current));
        // 2. Move the DOM marker in place.
        shipMarkerRef.current?.setLngLat([data.lng, data.lat]);
      },
    },
    { enabled: Boolean(ship && sseUrl) },
  );

  // ------------------------------------------------------------------
  // 5. Render
  // ------------------------------------------------------------------
  if (hasFailed) {
    return (
      <section
        className="outbreak-map-container"
        aria-label={t("map.ariaLabel", { count: regions.length })}
      >
        <output className="outbreak-map-fallback">
          <strong style={{ color: "var(--color-fg-default)" }}>{t("map.unavailableTitle")}</strong>
          <p style={{ margin: 0, maxWidth: "40ch" }}>{t("map.unavailableBody")}</p>
          <a
            href="#regions-table"
            style={{
              color: "var(--color-accent-info)",
              textDecoration: "underline",
            }}
          >
            {t("map.unavailableTableLink")}
          </a>
        </output>
      </section>
    );
  }

  return (
    <section
      className="outbreak-map-container"
      aria-label={t("map.ariaLabel", { count: regions.length })}
    >
      <div ref={containerRef} className="outbreak-map-canvas" data-testid="outbreak-map-canvas" />
      {!isStyleLoaded && (
        <div className="outbreak-map-skeleton" aria-hidden="true">
          {t("map.loading")}
        </div>
      )}
      {/* Visually hidden description for assistive tech. The visual map
          conveys the same info plus interaction; AT users get a count. */}
      <p
        style={{
          position: "absolute",
          inlineSize: "20px",
          blockSize: "1px",
          margin: "-1px",
          padding: 0,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          overflowWrap: "anywhere",
          border: 0,
        }}
      >
        {t("map.srDescription", { count: regions.length })}
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Helpers — exported for tests.
// ---------------------------------------------------------------------------

export function buildSSEUrl(mmsi: string, locale: string, apiBaseUrl?: string): string {
  const base =
    apiBaseUrl ??
    (typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
      ? (import.meta.env.VITE_API_URL as string | undefined)
      : undefined) ??
    "";
  const safeBase = base.replace(/\/+$/u, "");
  const safeLocale = encodeURIComponent(locale || "en");
  return `${safeBase}/sse/ships/${encodeURIComponent(mmsi)}?locale=${safeLocale}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function renderRegionPopup(
  region: { name: string; dataText: string | null; detailHref: string | null },
  t: TranslateFn,
): string {
  const parts: string[] = [];
  parts.push(
    `<strong style="font-family:var(--font-sans);font-size:0.95rem;">${escapeHtml(
      region.name,
    )}</strong>`,
  );
  if (region.dataText) {
    parts.push(
      `<p style="margin:0.5rem 0 0;color:var(--color-fg-muted);">${escapeHtml(
        region.dataText,
      )}</p>`,
    );
  }
  if (region.detailHref) {
    parts.push(
      `<p style="margin:0.625rem 0 0;"><a href="${escapeHtml(
        region.detailHref,
      )}" style="color:var(--color-accent-info);text-decoration:underline;">${escapeHtml(
        t("map.viewDetail"),
      )}</a></p>`,
    );
  }
  return `<div class="outbreak-map-popup">${parts.join("")}</div>`;
}
